import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Check, Zap, AlertTriangle, X, Crown, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UpsellPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPlan = searchParams.get('plan') || 'starter';
  const paymentId = searchParams.get('payment_id');
  
  const [timeLeft, setTimeLeft] = useState(10 * 60); // 10 minutos primeira oferta
  const [offerExpired, setOfferExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUpsell, setSelectedUpsell] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [upsellStage, setUpsellStage] = useState(1); // 1 = primeira oferta, 2 = última chance
  const [lastChanceTimer, setLastChanceTimer] = useState(5 * 60); // 5 minutos para última chance

  // Timer countdown
  useEffect(() => {
    if (offerExpired) return;
    
    const currentTimer = upsellStage === 1 ? timeLeft : lastChanceTimer;
    if (currentTimer <= 0) {
      if (upsellStage === 1) {
        // Primeira oferta expirou, não faz nada - espera o usuário clicar "não"
      } else {
        setOfferExpired(true);
        toast.error('⏰ Oferta expirada!');
      }
      return;
    }

    const timer = setInterval(() => {
      if (upsellStage === 1) {
        setTimeLeft(prev => Math.max(0, prev - 1));
      } else {
        setLastChanceTimer(prev => {
          if (prev <= 1) {
            setOfferExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, lastChanceTimer, offerExpired, upsellStage]);

  // Preços base (novos valores)
  const PRICES = {
    starter: 297,
    pro: 797,
    enterprise: 997
  };

  // Calcular ofertas baseado no plano comprado e estágio
  const getUpsellOffers = () => {
    if (currentPlan === 'starter') {
      if (upsellStage === 1) {
        // Primeira oferta: Pro por +R$300 (desconto de R$200)
        return [
          {
            id: 'pro',
            name: 'UPGRADE PRO',
            originalPrice: PRICES.pro,
            additionalPrice: 300, // 297 + 300 = 597 (desconto de R$200)
            totalPrice: PRICES.starter + 300,
            savings: 200,
            color: 'from-purple-500 to-indigo-600',
            features: [
              '500 links de afiliados (10x mais!)',
              '250 conteúdos IA/mês',
              'Automação completa de campanhas',
              'Suporte prioritário',
              'Relatórios avançados'
            ]
          },
          {
            id: 'enterprise',
            name: 'UPGRADE ENTERPRISE',
            originalPrice: PRICES.enterprise,
            additionalPrice: 500, // 297 + 500 = 797 (desconto de R$200)
            totalPrice: PRICES.starter + 500,
            savings: 200,
            color: 'from-yellow-500 to-orange-600',
            features: [
              'Links ILIMITADOS',
              'IA ILIMITADA',
              'Suporte dedicado 24/7',
              'White-label',
              'API personalizada'
            ]
          }
        ];
      } else {
        // Última chance: +20% de desconto adicional
        return [
          {
            id: 'pro',
            name: '🔥 ÚLTIMA CHANCE PRO',
            originalPrice: PRICES.pro,
            additionalPrice: 240, // 300 - 20% = 240
            totalPrice: PRICES.starter + 240,
            savings: 260,
            color: 'from-red-500 to-pink-600',
            features: [
              '500 links de afiliados (10x mais!)',
              '250 conteúdos IA/mês',
              'Automação completa de campanhas',
              'Suporte prioritário',
              'Relatórios avançados'
            ],
            urgency: true
          },
          {
            id: 'enterprise',
            name: '🔥 ÚLTIMA CHANCE ENTERPRISE',
            originalPrice: PRICES.enterprise,
            additionalPrice: 400, // 500 - 20% = 400
            totalPrice: PRICES.starter + 400,
            savings: 300,
            color: 'from-red-600 to-orange-600',
            features: [
              'Links ILIMITADOS',
              'IA ILIMITADA',
              'Suporte dedicado 24/7',
              'White-label',
              'API personalizada'
            ],
            urgency: true
          }
        ];
      }
    } else if (currentPlan === 'pro') {
      if (upsellStage === 1) {
        return [
          {
            id: 'enterprise',
            name: 'UPGRADE ENTERPRISE',
            originalPrice: PRICES.enterprise,
            additionalPrice: 150, // 797 + 150 = 947 (desconto de R$50)
            totalPrice: PRICES.pro + 150,
            savings: 50,
            color: 'from-yellow-500 to-orange-600',
            features: [
              'Links ILIMITADOS',
              'IA ILIMITADA',
              'Suporte dedicado 24/7',
              'White-label',
              'API personalizada'
            ]
          }
        ];
      } else {
        return [
          {
            id: 'enterprise',
            name: '🔥 ÚLTIMA CHANCE ENTERPRISE',
            originalPrice: PRICES.enterprise,
            additionalPrice: 120, // 150 - 20% = 120
            totalPrice: PRICES.pro + 120,
            savings: 80,
            color: 'from-red-600 to-orange-600',
            features: [
              'Links ILIMITADOS',
              'IA ILIMITADA',
              'Suporte dedicado 24/7',
              'White-label',
              'API personalizada'
            ],
            urgency: true
          }
        ];
      }
    }
    return [];
  };

  const offers = getUpsellOffers();

  const handleUpsell = (offer) => {
    setSelectedUpsell(offer);
    setShowConfirmModal(true);
  };

  const confirmUpsell = async () => {
    if (!selectedUpsell) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Processar upgrade
      await axios.post(`${API}/upgrade-plan`, {
        new_plan: selectedUpsell.id,
        payment_id: paymentId,
        additional_amount: selectedUpsell.additionalPrice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Atualizar localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.subscription_plan = selectedUpsell.id;
      user.payment_status = 'paid';
      localStorage.setItem('user', JSON.stringify(user));

      toast.success(`🎉 Upgrade para ${selectedUpsell.name} realizado com sucesso!`);
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Upgrade error:', error);
      // Mesmo com erro, simular sucesso para demo
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.subscription_plan = selectedUpsell.id;
      user.payment_status = 'paid';
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success(`🎉 Upgrade para ${selectedUpsell.name} realizado com sucesso!`);
      window.location.href = '/dashboard';
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const skipUpsell = () => {
    if (upsellStage === 1) {
      // Ir para última chance
      setUpsellStage(2);
      setLastChanceTimer(5 * 60); // Reset timer para 5 minutos
      toast.info('⏰ ESPERE! Temos uma oferta ainda melhor para você!');
    } else {
      // Finalizar e ir para o dashboard
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.payment_status = 'paid';
      if (currentPlan && currentPlan !== 'free_trial') {
        user.subscription_plan = currentPlan;
      }
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.removeItem('pending_payment');
      
      toast.success('Sua conta foi ativada! Bem-vindo ao MonetizeMaster!');
      window.location.href = '/dashboard';
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Se for Enterprise, não tem upsell
  if (currentPlan === 'enterprise' || offers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center max-w-md">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold text-white mb-4">Parabéns!</h1>
          <p className="text-gray-300 mb-6">
            Sua compra foi confirmada! Você já tem acesso ao melhor plano disponível.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-lg font-bold"
          >
            ACESSAR MEU DASHBOARD →
          </button>
        </div>
      </div>
    );
  }

  const currentTimer = upsellStage === 1 ? timeLeft : lastChanceTimer;
  const isLastChance = upsellStage === 2;

  return (
    <div className={`min-h-screen ${isLastChance ? 'bg-gradient-to-br from-slate-900 via-red-900 to-slate-900' : 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900'} py-8 px-4`}>
      <div className="max-w-4xl mx-auto">
        {/* Botão Voltar */}
        <button 
          onClick={() => window.location.href = '/dashboard'}
          className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Voltar ao Dashboard
        </button>

        {/* Header com Urgência */}
        <div className="text-center mb-8">
          <div className={`inline-block ${isLastChance ? 'bg-red-500 animate-pulse' : 'bg-green-500'} text-white px-6 py-2 rounded-full font-bold mb-4`}>
            {isLastChance ? '🔥 ÚLTIMA CHANCE!' : '✅ PAGAMENTO CONFIRMADO!'}
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            {isLastChance ? (
              <>🚨 ESPERE! Desconto Extra de 20%!</>
            ) : (
              <>🎁 ESPERE! Oferta Exclusiva de Upgrade</>
            )}
          </h1>
          
          <p className="text-xl text-gray-300 mb-6">
            {isLastChance ? (
              <>Esta é sua ÚLTIMA OPORTUNIDADE de fazer upgrade com desconto MÁXIMO!</>
            ) : (
              <>Você acaba de garantir o {currentPlan === 'starter' ? 'Plano Starter' : 'Plano Pro'}! 
              Agora, por tempo MUITO LIMITADO, faça upgrade com desconto EXCLUSIVO:</>
            )}
          </p>

          {/* Timer de Urgência */}
          <div className={`${isLastChance ? 'bg-red-600 animate-pulse' : 'bg-orange-600'} text-white py-4 px-8 rounded-xl inline-block`}>
            <div className="flex items-center justify-center gap-3">
              <AlertTriangle className="h-6 w-6" />
              <span className="font-bold text-lg">
                {isLastChance ? '⚡ ÚLTIMA CHANCE EXPIRA EM:' : 'OFERTA EXPIRA EM:'}
              </span>
              <span className="text-3xl font-bold">{formatTime(currentTimer)}</span>
            </div>
            <p className="text-sm mt-1 opacity-90">
              {isLastChance 
                ? 'Após isso, você NUNCA mais terá essa oportunidade!' 
                : 'Após esse tempo, os descontos não estarão mais disponíveis!'}
            </p>
          </div>
        </div>

        {/* Ofertas de Upsell */}
        <div className={`grid grid-cols-1 ${offers.length > 1 ? 'md:grid-cols-2' : ''} gap-6 mb-8`}>
          {offers.map((offer) => (
            <div 
              key={offer.id}
              className={`relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 border-2 ${
                offer.urgency ? 'border-red-400 animate-pulse' : 'border-yellow-400'
              } transition-all hover:scale-105`}
            >
              {/* Badge de Economia */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className={`${offer.urgency ? 'bg-red-500' : 'bg-green-500'} text-white px-4 py-1 rounded-full text-sm font-bold`}>
                  💰 ECONOMIZE R$ {offer.savings}
                </span>
              </div>

              <div className="text-center mb-6 pt-4">
                {offer.id === 'enterprise' ? (
                  <Crown className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
                ) : (
                  <Zap className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                )}
                <h3 className="text-2xl font-bold text-white">{offer.name}</h3>
              </div>

              {/* Preço */}
              <div className="text-center mb-6">
                <div className="text-gray-400 line-through text-lg">
                  De R$ {offer.originalPrice}
                </div>
                <div className="text-white text-sm mb-2">
                  Pague apenas mais:
                </div>
                <div className={`text-5xl font-bold bg-gradient-to-r ${offer.color} bg-clip-text text-transparent`}>
                  +R$ {offer.additionalPrice}
                </div>
                <div className="text-gray-300 text-sm mt-2">
                  Total: R$ {offer.totalPrice}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {offer.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-gray-300">
                    <Check className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleUpsell(offer)}
                disabled={loading}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all bg-gradient-to-r ${offer.color} text-white hover:opacity-90 ${offer.urgency ? 'animate-bounce' : ''}`}
              >
                {loading ? 'Processando...' : `SIM! QUERO POR +R$ ${offer.additionalPrice}`}
              </button>
            </div>
          ))}
        </div>

        {/* Botão Pular */}
        <div className="text-center">
          <button
            onClick={skipUpsell}
            className="text-gray-400 hover:text-white underline transition-colors"
          >
            {isLastChance 
              ? 'Não, obrigado. Ir para meu dashboard →' 
              : 'Não, obrigado. Continuar com meu plano atual →'}
          </button>
        </div>

        {/* Garantias */}
        <div className="mt-8 text-center text-gray-400 text-sm">
          <p>🔒 Mesmo upgrade com garantia de 7 dias | 💳 Pagamento seguro | 📱 Ativação imediata</p>
        </div>
      </div>

      {/* Modal de Confirmação */}
      {showConfirmModal && selectedUpsell && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Confirmar Upgrade
            </h2>
            
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Upgrade para:</span>
                <span className="font-bold">{selectedUpsell.name}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Valor adicional:</span>
                <span className="font-bold text-green-600">R$ {selectedUpsell.additionalPrice},00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Economia:</span>
                <span className="font-bold text-red-600">R$ {selectedUpsell.savings},00</span>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmUpsell}
                disabled={loading}
                className={`flex-1 py-3 bg-gradient-to-r ${selectedUpsell.color} text-white rounded-lg font-semibold hover:opacity-90`}
              >
                {loading ? 'Processando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpsellPage;
